import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { ApplianceDTO } from '@webip/shared/src/dto/appliance';
import { useLocalStorage } from './useLocalStorage';
import { getTenantFromWindowPathname } from './tenant';

export type ApplianceContextValue = {
    applianceId: string | null;
    setApplianceId: (id: string | null) => void;
    appliances: ApplianceDTO[];
    applianceOptions: { id: string; label: string }[];
    selectedAppliance: ApplianceDTO | null;
    isLoading: boolean;
};

export const ApplianceContext = createContext<ApplianceContextValue>({
    applianceId: null,
    setApplianceId: () => { },
    appliances: [],
    applianceOptions: [],
    selectedAppliance: null,
    isLoading: false,
});

export const ApplianceProvider = ({ children }: { children: React.ReactNode }) => {
    const tenant = getTenantFromWindowPathname();
    const storageKey = tenant ? `webip.selected_appliance.${tenant}` : 'webip.selected_appliance';

    const [applianceId, _setApplianceId] = useLocalStorage<string | null>(storageKey, null);

    // Normaliza para string, rejeita nulos e vazios (zero é ID válido)
    const sanitizedApplianceId = useMemo(() => {
        if (applianceId == null) return null;
        const str = String(applianceId).trim();
        return /^\d+$/.test(str) && Number(str) >= 0 ? str : null;
    }, [applianceId]);

    // Setter estável que sempre persiste como string
    const setApplianceId = useCallback((id: string | null) => {
        _setApplianceId(id != null ? String(id) : null);
    }, [_setApplianceId]);

    const authQuery = useQuery(['auth-check'], async () => {
        const res = await axios.get('/api/auth/me', { withCredentials: true });
        return res.data?.user ?? null;
    }, { retry: false });

    const isAuthenticated = !!authQuery.data;

    const { data, isLoading } = useQuery(['appliances'], async () => {
        const res = await axios.get<{ ok: boolean; result: ApplianceDTO[] }>('/api/appliance', {
            withCredentials: true,
        });
        return res.data.result ?? [];
    }, {
        enabled: isAuthenticated,
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const appliances = useMemo(() => data ?? [], [data]);
    const applianceOptions = useMemo(
        () => appliances.map((a) => ({ id: String(a.id), label: a.nome })),
        [appliances]
    );

    // Auto-seleciona: mantém a última usada se ainda existe, senão seleciona a primeira disponível
    useEffect(() => {
        if (isLoading || appliances.length === 0) return;
        const ids = appliances.map((a) => String(a.id));
        if (sanitizedApplianceId && ids.includes(sanitizedApplianceId)) return;
        setApplianceId(ids[0]);
    }, [appliances, isLoading, sanitizedApplianceId, setApplianceId]);

    const selectedAppliance = appliances.find((a) => String(a.id) === sanitizedApplianceId) ?? null;

    return (
        <ApplianceContext.Provider
            value={{ applianceId: sanitizedApplianceId, setApplianceId, appliances, applianceOptions, selectedAppliance, isLoading }}
        >
            {children}
        </ApplianceContext.Provider>
    );
};
